import getServiceWorkerPath from './getServiceWorkerPath';
import getRootVdirName from 'owa-url/lib/getRootVdirName';
import { hasQueryStringParameter, getQueryStringParameter } from 'owa-querystring';
import { trace } from 'owa-trace';
import {
    tryInitializeServiceWorkerCacheInstaller,
    installServiceWorkerCache,
} from './installServiceWorkerCache';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getBrowserInfo, Browser } from 'owa-user-agent';

const SERVICEWORKER_FILENAME: string = 'sw.js';
const SERVICEWORKER_ACTIVATED: string = 'activated';

let activeRegistration: ServiceWorkerRegistration = null;
let serviceWorkerContainer: ServiceWorkerContainer = null;
let windowObj: Window = null;
let serviceWorkerPath: string = null;
let currentAppName: string = null;
let currentUserLanguage: string = null;
let currentDarkMode: boolean = false;

export default function registerServiceworker(
    windowObject: Window,
    appName: string,
    userLanguage: string,
    darkMode?: boolean
): void {
    windowObj = windowObject;
    serviceWorkerContainer = windowObj.navigator.serviceWorker;
    let rootVdir = getRootVdirName(windowObj.location);
    serviceWorkerPath = getServiceWorkerPath(rootVdir, windowObj.location);
    currentAppName = appName;
    currentUserLanguage = userLanguage;
    currentDarkMode = darkMode;
    let serviceworkerUrl =
        serviceWorkerPath != null
            ? `${serviceWorkerPath}/${SERVICEWORKER_FILENAME}`
            : `/${SERVICEWORKER_FILENAME}`;
    let options: RegistrationOptions = rootVdir != null ? { scope: '/' + rootVdir + '/' } : null;
    let params = [];

    ['branch', 'ring', 'version'].some(el => {
        if (hasQueryStringParameter(el, windowObject.location)) {
            params.push(`${el}=${getQueryStringParameter(el, windowObject.location)}`);
            return true;
        }

        return false;
    });

    // fa=1 will tell the service worker, in serviceworkerlib, whether this flight is enabled
    // we also post a message to it, during the install phase, but that can happen significantly
    // after boot.
    isFeatureEnabled('fwk-sw-proxyFetchAlways') && proxyFetchForBrowser() && params.push('fa=1');

    if (params.length > 0) {
        serviceworkerUrl = `${serviceworkerUrl}?${params.join('&')}`;
    }

    serviceWorkerContainer
        .register(serviceworkerUrl, options)
        .then(function(registration: ServiceWorkerRegistration) {
            trace.info('[SW Client] service worker registration succeeded');
        })
        .catch(function(e: Error) {
            trace.warn('[SW Client] service worker registraiton failed, error: ' + e.message);
        });

    serviceWorkerContainer.ready.then(function(registration: ServiceWorkerRegistration) {
        serviceWorkerReady(registration);
    });
}

function serviceWorkerReady(registration: ServiceWorkerRegistration): void {
    trace.info('[SW Client] ready fired, state: ' + registration.active.state);
    activeRegistration = registration;
    serviceWorkerContainer.oncontrollerchange = serviceWorkerControllerChanged;
    serviceWorkerActivating();
}

function serviceWorkerActivating(): void {
    if (activeRegistration.active.state == SERVICEWORKER_ACTIVATED) {
        serviceWorkerActivated();
    } else {
        activeRegistration.active.onstatechange = serviceWorkerStateChanged;
    }
}

function serviceWorkerStateChanged(event: Event): void {
    if (activeRegistration.active.state == SERVICEWORKER_ACTIVATED) {
        activeRegistration.active.onstatechange = null;
        serviceWorkerActivated();
    }
}

function serviceWorkerControllerChanged(): void {
    if (activeRegistration.active != null) {
        serviceWorkerActivating();
    }
}

function serviceWorkerActivated(): void {
    tryInitializeServiceWorkerCacheInstaller(
        {
            serviceWorkerContainer: serviceWorkerContainer,
            serviceWorkerRegistration: activeRegistration,
            serviceWorkerPath: serviceWorkerPath,
            appName: currentAppName,
            userLanguage: currentUserLanguage,
            darkMode: currentDarkMode,
        },
        windowObj
    );

    installServiceWorkerCache();
}

function proxyFetchForBrowser(): boolean {
    const { browser, browserVersion } = getBrowserInfo();
    return (
        (browser == Browser.CHROME || browser == Browser.EDGE_CHROMIUM) &&
        browserVersion &&
        browserVersion.length > 0 // && browserVersion[0] < 76
    );
}
